import { Button, Input, Modal, Spin, Switch, Table } from "antd";
import React, { useCallback, useState } from "react";
import { FloorplanByIdQuery } from "../../generated/types";
import {
  BIM_ORIGIN_X_TOOLTIP_TEXT,
  BIM_ORIGIN_Y_TOOLTIP_TEXT,
  BIM_ORIGIN_Z_TOOLTIP_TEXT,
  FLOOR_HEIGHT_TOOLTIP_TEXT,
  TRUE_NORTH_TOOLTIP_TEXT,
} from "./ImportFloorplanModal/ImportFloorplanModal";
import { InputRow } from "./ImportFloorplanModal/SetPointRow";
import { FloorplanEditPropertiesInput } from "./hooks/useFloorplan";

const BIM_ORIGIN_X = "BIM Origin X";
const BIM_ORIGIN_Y = "BIM Origin Y";
const BIM_ORIGIN_Z = "BIM Origin Z";
const FLOOR_HEIGHT = "Floor Height";
const ANGLE_TO_TRUE_NORTH = "Angle to True North";
const BIM_PROJECT_NAME = "BIM Project ID";

interface EditPropertiesModalProps {
  floorplan: FloorplanByIdQuery["floorplans"][0] | undefined;
  onClose: () => void;
  editFloorplanProperties: (
    floorplan: Omit<FloorplanEditPropertiesInput, "id">,
  ) => Promise<void>;
  spaceName: string;
  uploadInProgress: boolean;
  bimMode?: boolean;
  editSpaceBimProperties: (bimFloorModels: BimFloorModel[]) => Promise<void>;
  spaceBimModels: BimFloorModel[];
}
type BimFloorModel = {
  label: string;
  bimFloorId: string;
  isCommon: boolean;
};

export const EditPropertiesModal = (props: EditPropertiesModalProps) => {
  const {
    onClose,
    editFloorplanProperties,
    spaceName,
    uploadInProgress,
    floorplan,
    bimMode = false,
    editSpaceBimProperties,
    spaceBimModels,
  } = props;

  const {
    bimOriginX: currentBimOriginX,
    bimOriginY: currentBimOriginY,
    bimOriginZ: currentBimOriginZ,
    floorHeight: currentFloorHeight,
    angleToTrueNorth: currentAngleToTrueNorth,
    bimProjectId: currentBimProjectId,
    bimFloorModels: currentBimFloorModels,
  } = floorplan ?? {};
  const [bimOriginX, setBimOriginX] = useState<number | undefined>(
    currentBimOriginX ?? undefined,
  );
  const [bimOriginY, setBimOriginY] = useState<number | undefined>(
    currentBimOriginY ?? undefined,
  );
  const [bimOriginZ, setBimOriginZ] = useState<number | undefined>(
    currentBimOriginZ ?? undefined,
  );
  const [floorHeight, setFloorHeight] = useState<number | undefined>(
    currentFloorHeight ?? undefined,
  );
  const [angleToTrueNorth, setAngleToTrueNorth] = useState<number>(
    currentAngleToTrueNorth ?? 0,
  );
  const [bimProjectId, setBimProjectId] = useState<string>(
    currentBimProjectId ?? "",
  );
  const [bimFloorModels, setBimFloorModels] = useState<BimFloorModel[]>(
    bimMode
      ? spaceBimModels
      : currentBimFloorModels && currentBimFloorModels.length > 0
      ? currentBimFloorModels.map((entry) => {
          return {
            label: entry.label,
            bimFloorId: entry.bimFloorId,
            isCommon: entry.isCommon ?? true,
          };
        })
      : [],
  );

  const columnsBimFloorModels = [
    {
      title: "BIM Model Name",
      dataIndex: "label",
      key: "label",
      render: (_text: string, record: BimFloorModel, index: number) => (
        <Input
          value={record.label}
          onChange={(e) => handleSetBimFloorModel(index, e.target.value, true)}
        />
      ),
    },
    {
      title: "BIM Model ID",
      dataIndex: "bimFloorId",
      key: "bimFloorId",
      render: (_text: string, record: BimFloorModel, index: number) => (
        <Input
          value={record.bimFloorId}
          onChange={(e) => handleSetBimFloorModel(index, e.target.value, false)}
        />
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_text: string, record: BimFloorModel, index: number) => (
        <>
          <Switch
            checkedChildren="Common"
            unCheckedChildren="Uncommon"
            checked={record.isCommon === true ? true : false}
            onChange={(checked) => handleSetBimFloorModelCommon(index, checked)}
          />
          <Button type="link" onClick={() => handleRemoveBimFloorModel(index)}>
            Remove
          </Button>
        </>
      ),
    },
  ];

  const handleRemoveBimFloorModel = (id: number) => {
    const tempBimFloorModels = [...bimFloorModels];
    tempBimFloorModels.splice(id, 1);
    setBimFloorModels(tempBimFloorModels);
  };
  const handleAddBimFloorModel = () => {
    const newBimFloorModel = {
      label: "",
      bimFloorId: "",
      isCommon: false,
    };
    setBimFloorModels([...bimFloorModels, newBimFloorModel]);
  };
  const handleSetBimFloorModel = (
    id: number,
    text: string,
    isLabel: boolean,
  ) => {
    const tempBimFloorModels = [...bimFloorModels];
    tempBimFloorModels[id] = {
      ...tempBimFloorModels[id],
      [isLabel ? "label" : "bimFloorId"]: text,
    };
    setBimFloorModels(tempBimFloorModels);
  };

  const handleSetBimFloorModelCommon = (id: number, isCommon: boolean) => {
    const tempBimFloorModels = [...bimFloorModels];
    tempBimFloorModels[id] = {
      ...tempBimFloorModels[id],
      isCommon,
    };
    setBimFloorModels(tempBimFloorModels);
  };

  const hostname = window.location.hostname;
  const regionMatch = hostname.match(/\.([a-z]{2,3})\.disperse\.io/);

  const SPECKLE_VIEWER_BASE_URL = `https://speckle.${
    regionMatch?.[1] || "uk"
  }.disperse.io`;

  const onConfirm = useCallback(() => {
    if (bimMode) {
      editSpaceBimProperties(bimFloorModels);
    } else {
      const editedFloorplan = {
        bimOriginX,
        bimOriginY,
        bimOriginZ:
          bimOriginZ === undefined &&
          bimOriginY !== undefined &&
          bimOriginX !== undefined
            ? 0
            : bimOriginZ,
        floorHeight,
        angleToTrueNorth,
        bimProjectId,
        bimFloorModels,
      };
      editFloorplanProperties(editedFloorplan);
    }
  }, [
    bimOriginX,
    bimOriginY,
    bimOriginZ,
    floorHeight,
    angleToTrueNorth,
    bimProjectId,
    bimFloorModels,
    editFloorplanProperties,
    bimMode,
    editSpaceBimProperties,
  ]);

  const previewUrl = `${SPECKLE_VIEWER_BASE_URL}/projects/${
    encodeURIComponent(bimProjectId) || "<project-id>"
  }/models/${bimFloorModels
    .map((model) => encodeURIComponent(model.bimFloorId))
    .join(",")}`;
  return (
    <Modal
      bodyStyle={{ height: "45vh", overflowY: "auto" }}
      width={700}
      title={`Edit ${bimMode ? "BIM Models" : "floorplan"} for ${spaceName}`}
      closable={false}
      visible={true}
      onCancel={onClose}
      destroyOnClose={true}
      onOk={onConfirm}
      okText={"Confirm"}
    >
      <Spin spinning={uploadInProgress} tip="Upload in progress...">
        {!bimMode && (
          <>
            <InputRow
              name={BIM_ORIGIN_X}
              value={bimOriginX}
              onSet={setBimOriginX}
              unit={"mm"}
              tooltipText={BIM_ORIGIN_X_TOOLTIP_TEXT}
            />
            <InputRow
              name={BIM_ORIGIN_Y}
              value={bimOriginY}
              onSet={setBimOriginY}
              unit={"mm"}
              tooltipText={BIM_ORIGIN_Y_TOOLTIP_TEXT}
            />
            <InputRow
              name={BIM_ORIGIN_Z}
              value={bimOriginZ}
              onSet={setBimOriginZ}
              unit={"mm"}
              tooltipText={BIM_ORIGIN_Z_TOOLTIP_TEXT}
            />
            <InputRow
              name={FLOOR_HEIGHT}
              value={floorHeight}
              onSet={setFloorHeight}
              unit={"mm"}
              tooltipText={FLOOR_HEIGHT_TOOLTIP_TEXT}
            />
            <InputRow
              name={ANGLE_TO_TRUE_NORTH}
              value={angleToTrueNorth}
              onSet={setAngleToTrueNorth}
              unit={"degrees"}
              tooltipText={TRUE_NORTH_TOOLTIP_TEXT}
            />
            <InputRow
              name={BIM_PROJECT_NAME}
              value={bimProjectId}
              onSet={setBimProjectId}
              unit={""}
              tooltipText="Enter the BIM project ID."
              type="string"
            />
          </>
        )}

        <Button
          style={{ marginTop: "10px", float: "right" }}
          type="primary"
          onClick={handleAddBimFloorModel}
        >
          Add BIM Model
        </Button>
        <Table
          dataSource={bimFloorModels}
          columns={columnsBimFloorModels}
          pagination={false}
        />
        <div
          style={{
            marginTop: "16px",
            padding: "8px",
            backgroundColor: "#f0f0f0",
            borderRadius: "4px",
          }}
        >
          <strong>Preview URL:</strong>
          <div>
            <a href={previewUrl} target="_blank" rel="noopener noreferrer">
              {previewUrl}
            </a>
          </div>
        </div>
      </Spin>
    </Modal>
  );
};
